'use client';

import { Builder, withChildren } from '@builder.io/react';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import { CheckCircle } from '@videoblocks/react-icons';

import { ProgressiveImage } from '../../../storywind';
import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';

export interface IValuePropsProps {
  valueProps: Array<{
    text: string;
    icon: string;
  }>;
}

const ValueProps = withChildren(({ valueProps = [] }: IValuePropsProps) => {
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [totalWidth, setTotalWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const setRef = (el: HTMLDivElement | null, index: number) => {
    if (el) {
      itemRefs.current[index] = el;
    }
  };
  const setWidth = useCallback(
    (itemRefs: React.MutableRefObject<Array<HTMLDivElement | null>>) => {
      if (!containerRef.current) {
        return;
      }
      const containerStyles = window.getComputedStyle(containerRef.current);
      const gapValue = parseFloat(containerStyles.gap) || 0;
      setTotalWidth(
        itemRefs.current.reduce(
          (acc, item) => acc + (item?.scrollWidth || 0),
          0
        ) +
          gapValue * (itemRefs.current.length - 1)
      );
    },
    []
  );
  useEffect(() => {
    if (itemRefs.current.length > 0 && containerRef.current) {
      setWidth(itemRefs);
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, [setWidth]);
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
        setWidth(itemRefs);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setWidth, valueProps]);

  const styledContainer = twMerge(
    'flex snap-x scroll-pl-6 gap-12 overflow-x-auto py-12 md:px-12 lg:px-24 justify-center',
    totalWidth >= containerWidth && 'justify-start'
  );
  const styledItem = twMerge(
    'shrink-0 snap-start flex items-center gap-3',
    totalWidth >= containerWidth && 'first:pl-6 last:pr-6'
  );
  return (
    <section className={styledContainer} ref={containerRef}>
      {valueProps.map(({ text, icon }, index) => (
        <div
          key={`value-prop-${index}`}
          className={styledItem}
          ref={(el) => setRef(el, index)}
        >
          {icon ? (
            <ProgressiveImage
              src={icon}
              alt={text}
              className="w-8 h-8 object-cover"
            />
          ) : (
            <CheckCircle className="w-8 h-8 text-gray-400" />
          )}
          <Text
            fontFamily="polysans"
            fontSize="lg"
            className="text-nowrap mb-0 text-gray-900"
          >
            <span>{text}</span>
          </Text>
        </div>
      ))}
    </section>
  );
});

export default ValueProps;

Builder.registerComponent(ValueProps, {
  name: 'Value Props',
  inputs: [
    {
      name: 'valueProps',
      type: 'list',
      subFields: [
        {
          name: 'text',
          type: 'string',
          required: true,
          helperText: 'The text for the value prop.',
        },
        {
          name: 'icon',
          type: 'file',
          allowedFileTypes: ['png', 'jpg', 'jpeg', 'svg'],
          required: true,
          helperText: 'The icon for the value prop.',
        },
      ],
      onChange: (options) => {
        if (options.get('valueProps').length > 3) {
          options.set('valueProps', options.get('valueProps').slice(0, 3));
          alert(
            'Maximum number of value props is 3. Modify an existing value prop instead.'
          );
        }
      },
      required: true,
      defaultValue: [
        {
          text: 'Value Prop Text',
          icon: '',
        },
      ],
      helperText: 'List of value props to display.',
    },
  ],
});
